import { useAuth } from "@clerk/clerk-react";
import { clerkEnabled } from "@/services/clerk";

/**
 * Whether Clerk has finished loading, and who it says this is.
 *
 * Every API call attaches the Clerk session JWT, and `getClerkToken()` returns
 * null while `window.Clerk` is still coming up. So a query that fires before
 * Clerk has loaded goes out unauthenticated and comes back as "signed out" —
 * which used to be impossible, because every page waited behind a session
 * gate, and became a race the moment the app started rendering for signed-out
 * readers. It showed as a Sign in button on a page you had just signed into,
 * *sometimes*.
 *
 * The fix is two-part and both halves live here: nothing fetches until
 * `isLoaded`, and the user id goes in the query key, so signing in and signing
 * out are different cache entries rather than one entry that has to be
 * invalidated by hand.
 *
 * Split around the module constant because `useAuth` throws with no
 * ClerkProvider above it, and with no publishable key `main.tsx` renders
 * without one. The constant cannot change between renders, so the hook order
 * is stable.
 */
export type ClerkSession = { isLoaded: boolean; userId: string | null };

function useClerkAuthSession(): ClerkSession {
  const { isLoaded, userId } = useAuth();
  return { isLoaded, userId: userId ?? null };
}

export function useClerkSession(): ClerkSession {
  // eslint-disable-next-line react-hooks/rules-of-hooks -- constant per build
  return clerkEnabled ? useClerkAuthSession() : { isLoaded: true, userId: null };
}

export default useClerkSession;
